/**
 * RulesPage — Steward Admin
 * Create and manage championship rules with a rich text (HTML) editor.
 * Rules are stored in championships/data/rules and can be linked to events/rounds.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { onRules, createRule, updateRule, deleteRule } from '../services/tripleCrown';

/* ══════════════════════════════════════
   RICH TEXT TOOLBAR
   ══════════════════════════════════════ */
const TOOLBAR = [
  { cmd: 'bold', icon: 'B', style: 'font-weight:800', title: 'Bold' },
  { cmd: 'italic', icon: 'I', style: 'font-style:italic', title: 'Italic' },
  { cmd: 'underline', icon: 'U', style: 'text-decoration:underline', title: 'Underline' },
  { cmd: 'strikeThrough', icon: 'S', style: 'text-decoration:line-through', title: 'Strikethrough' },
  '|',
  { cmd: 'formatBlock:H2', icon: 'H2', title: 'Heading 2' },
  { cmd: 'formatBlock:H3', icon: 'H3', title: 'Heading 3' },
  { cmd: 'formatBlock:P', icon: '¶', title: 'Paragraph' },
  '|',
  { cmd: 'insertUnorderedList', icon: '•', title: 'Bullet list' },
  { cmd: 'insertOrderedList', icon: '1.', title: 'Numbered list' },
  '|',
  { cmd: 'justifyLeft', icon: '⫷', title: 'Align left' },
  { cmd: 'justifyCenter', icon: '⫸', title: 'Align center' },
  '|',
  { cmd: 'foreColor:#12d0ff', icon: 'A', style: 'color:#12d0ff;font-weight:700', title: 'Teal highlight' },
  { cmd: 'foreColor:#ff2d78', icon: 'A', style: 'color:#ff2d78;font-weight:700', title: 'Pink highlight' },
  { cmd: 'foreColor:#ffc857', icon: 'A', style: 'color:#ffc857;font-weight:700', title: 'Gold highlight' },
  { cmd: 'foreColor:#eaf2ff', icon: 'A', style: 'color:#eaf2ff;font-weight:700', title: 'White text' },
  '|',
  { cmd: 'removeFormat', icon: '⊘', title: 'Clear formatting' },
];

function ToolbarButton({ item, onExec }) {
  return (
    <button
      type="button"
      className="rte-btn"
      title={item.title}
      style={item.style ? Object.fromEntries(item.style.split(';').filter(Boolean).map(s => {
        const [k, v] = s.split(':');
        return [k.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase()), v.trim()];
      })) : undefined}
      onMouseDown={e => { e.preventDefault(); onExec(item.cmd); }}
    >
      {item.icon}
    </button>
  );
}

/* ══════════════════════════════════════
   RICH TEXT EDITOR
   ══════════════════════════════════════ */
function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  // Set initial content
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const handleInput = useCallback(() => {
    isInternalChange.current = true;
    onChange(editorRef.current.innerHTML);
    requestAnimationFrame(() => { isInternalChange.current = false; });
  }, [onChange]);

  const exec = useCallback((cmd) => {
    if (cmd.startsWith('formatBlock:')) {
      document.execCommand('formatBlock', false, `<${cmd.split(':')[1]}>`);
    } else if (cmd.startsWith('foreColor:')) {
      document.execCommand('foreColor', false, cmd.split(':')[1]);
    } else {
      document.execCommand(cmd, false, null);
    }
    handleInput();
    editorRef.current?.focus();
  }, [handleInput]);

  const handlePaste = useCallback((e) => {
    // Allow rich paste from clipboard (preserve formatting)
    const html = e.clipboardData.getData('text/html');
    if (html) {
      e.preventDefault();
      document.execCommand('insertHTML', false, html);
      handleInput();
    }
  }, [handleInput]);

  const handleKeyDown = useCallback((e) => {
    // Ctrl/Cmd+B, I, U shortcuts
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
      if (e.key === 'b') { e.preventDefault(); exec('bold'); }
      if (e.key === 'i') { e.preventDefault(); exec('italic'); }
      if (e.key === 'u') { e.preventDefault(); exec('underline'); }
    }
  }, [exec]);

  return (
    <div className="rte-wrapper">
      <div className="rte-toolbar">
        {TOOLBAR.map((item, i) =>
          item === '|'
            ? <span key={i} className="rte-sep" />
            : <ToolbarButton key={i} item={item} onExec={exec} />
        )}
      </div>
      <div
        ref={editorRef}
        className="rte-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder || 'Start typing rules...'}
      />
    </div>
  );
}

/* ══════════════════════════════════════
   HTML SOURCE EDITOR
   ══════════════════════════════════════ */
function HtmlSourceEditor({ value, onChange }) {
  return (
    <textarea
      className="form-input rte-source"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder="<h2>Section Title</h2><p>Rule content here...</p>"
      rows={16}
    />
  );
}

/* ══════════════════════════════════════
   RULE EDITOR (create / edit)
   ══════════════════════════════════════ */
function RuleEditor({ rule, onSave, onCancel }) {
  const [title, setTitle] = useState(rule?.title || '');
  const [content, setContent] = useState(rule?.content || '');
  const [mode, setMode] = useState('visual'); // 'visual' | 'html'
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave({ title: title.trim(), content });
    } finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="card-header">
        <h3 className="card-title">{rule ? 'Edit Rule' : 'New Rule'}</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            className={`btn btn-sm ${mode === 'visual' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setMode('visual')}
          >Visual</button>
          <button
            type="button"
            className={`btn btn-sm ${mode === 'html' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setMode('html')}
          >HTML</button>
        </div>
      </div>

      <div style={{ padding: '0 20px 16px' }}>
        <div className="form-group">
          <label className="form-label">Rule Title *</label>
          <input
            className="form-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. GT7 Battle Royale — Official Rules"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Content</label>
          {mode === 'visual'
            ? <RichTextEditor value={content} onChange={setContent} />
            : <HtmlSourceEditor value={content} onChange={setContent} />
          }
        </div>

        {/* Live preview */}
        {content && (
          <div className="form-group">
            <label className="form-label" style={{ marginBottom: 8 }}>Preview</label>
            <div className="rte-preview" dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !title.trim()}>
            {saving ? 'Saving...' : rule ? 'Update Rule' : 'Create Rule'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════ */
export default function RulesPage() {
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null);    // null | 'new' | rule object
  const [expandedId, setExpandedId] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => onRules(setRules), []);
  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }
  }, [toast]);

  const handleSave = async (data) => {
    try {
      if (editing && editing !== 'new') {
        await updateRule(editing.id, data);
        setToast({ type: 'success', msg: 'Rule updated!' });
      } else {
        await createRule(data);
        setToast({ type: 'success', msg: 'Rule created!' });
      }
      setEditing(null);
    } catch (err) {
      setToast({ type: 'error', msg: err?.message || 'Failed to save rule' });
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.title}"? This cannot be undone.`)) return;
    try {
      await deleteRule(rule.id);
      setToast({ type: 'success', msg: 'Rule deleted' });
      if (expandedId === rule.id) setExpandedId(null);
    } catch (err) {
      setToast({ type: 'error', msg: err?.message || 'Failed to delete' });
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title">Rules</h2>
          <p className="page-subtitle">Manage championship rules — assign them to events and rounds</p>
        </div>
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`} style={{ marginBottom: 16 }}>{toast.msg}</div>
      )}

      {/* Editor */}
      {editing ? (
        <RuleEditor
          rule={editing === 'new' ? null : editing}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      ) : (
        <div style={{ marginBottom: 18 }}>
          <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>+ New Rule</button>
        </div>
      )}

      {/* Rules List */}
      {rules.length === 0 && !editing ? (
        <div className="card" style={{ padding: '48px 20px', textAlign: 'center' }}>
          <p style={{ fontSize: 15, color: 'var(--text-muted)', margin: 0 }}>
            No rules yet. Create one to get started.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rules.map(rule => (
            <div key={rule.id} className="card" style={{ overflow: 'hidden' }}>
              <div
                className="card-header"
                style={{ cursor: 'pointer' }}
                onClick={() => setExpandedId(expandedId === rule.id ? null : rule.id)}
              >
                <div>
                  <h3 className="card-title" style={{ margin: 0 }}>{rule.title}</h3>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                    ID: {rule.id}
                    {rule.updatedAt?.toDate && ` · Updated ${rule.updatedAt.toDate().toLocaleDateString(undefined, { timeZone: 'America/Barbados' })}`}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); setEditing(rule); }}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={e => { e.stopPropagation(); handleDelete(rule); }}>Delete</button>
                  <span style={{ fontSize: 18, color: 'var(--text-muted)', marginLeft: 4 }}>
                    {expandedId === rule.id ? '▾' : '▸'}
                  </span>
                </div>
              </div>

              {expandedId === rule.id && (
                <div style={{ padding: '0 20px 20px' }}>
                  <div className="rte-preview" dangerouslySetInnerHTML={{ __html: rule.content || '<em>No content</em>' }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Inline styles for the rich text editor */}
      <style>{`
        /* ── Rich Text Editor ── */
        .rte-wrapper {
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-md);
          overflow: hidden;
          background: var(--bg-input);
        }
        .rte-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: 2px;
          padding: 6px 8px;
          background: var(--bg-elevated);
          border-bottom: 1px solid var(--border);
          align-items: center;
        }
        .rte-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 28px;
          border: none;
          border-radius: 4px;
          background: transparent;
          color: var(--text-soft);
          font-size: 13px;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
        }
        .rte-btn:hover {
          background: rgba(255,255,255,0.08);
          color: var(--teal);
        }
        .rte-sep {
          width: 1px;
          height: 20px;
          background: var(--border);
          margin: 0 4px;
          flex-shrink: 0;
        }
        .rte-editor {
          min-height: 280px;
          max-height: 600px;
          overflow-y: auto;
          padding: 16px 20px;
          color: var(--text);
          font-size: 14px;
          line-height: 1.7;
          outline: none;
        }
        .rte-editor:empty::before {
          content: attr(data-placeholder);
          color: var(--text-muted);
          pointer-events: none;
        }
        .rte-editor h2 {
          font-size: 20px;
          font-weight: 700;
          margin: 20px 0 8px;
          color: var(--teal);
        }
        .rte-editor h3 {
          font-size: 16px;
          font-weight: 700;
          margin: 16px 0 6px;
          color: var(--gold);
        }
        .rte-editor p {
          margin: 0 0 10px;
        }
        .rte-editor ul, .rte-editor ol {
          margin: 0 0 10px;
          padding-left: 24px;
        }
        .rte-editor li {
          margin-bottom: 4px;
        }
        .rte-source {
          min-height: 280px;
          font-family: 'SF Mono', 'Fira Code', monospace;
          font-size: 13px;
          line-height: 1.6;
          white-space: pre-wrap;
          resize: vertical;
        }

        /* ── Preview pane ── */
        .rte-preview {
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: 20px 24px;
          color: var(--text);
          font-size: 14px;
          line-height: 1.7;
          max-height: 400px;
          overflow-y: auto;
        }
        .rte-preview h2 {
          font-size: 20px;
          font-weight: 700;
          margin: 20px 0 8px;
          color: var(--teal);
        }
        .rte-preview h3 {
          font-size: 16px;
          font-weight: 700;
          margin: 16px 0 6px;
          color: var(--gold);
        }
        .rte-preview p {
          margin: 0 0 10px;
        }
        .rte-preview ul, .rte-preview ol {
          margin: 0 0 10px;
          padding-left: 24px;
        }
        .rte-preview li {
          margin-bottom: 4px;
        }
      `}</style>
    </div>
  );
}
